import {
  ApiDashboardElement,
  FreeColumnJSON,
  TableConfigured,
} from '@highimpact-frontend/api-types';
import type { MeasureType } from '@highimpact/dashboard-types';
import { StateComponent } from '@highimpact/dashboard-ui';
import { fromArray } from '@highimpact/data-utils/records';
import { Alert, Button, ButtonGroup } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  FocusEventHandler,
  forwardRef,
  KeyboardEventHandler,
  LegacyRef,
  ReactEventHandler,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useDashboardHost,
  useDashboardScope,
} from '../../../../context/DashboardHostContext';
import { groupBy, unique } from '../../../../internal/utils/array';
import { not } from '../../../../internal/utils/predicate';
import { map, mapKeys, toArray } from '../../../../internal/utils/record';
import { resolveElementIntervalNames } from '../../../../runtime/buildElementPlotMetadata';
import type { DashboardLibraryDispatch } from '../../../../runtime/DashboardLibraryProvider';
import { useDashboardLibraryDispatch } from '../../../../runtime/DashboardLibraryProvider';
import type { MeasureRow } from '../../../../runtime/measureRow.types';
import { updateAndPersistDashboardElement } from '../../../../state/asyncThunks';
import {
  AdditionalColumn,
  getAdditionalColumns,
  getAggregationTableColumnsByPredicateE,
  insertFreeColumnAtEachRowE,
  isAggregatedColumn,
  isAggregatedTable,
  isFreeColumn,
  makeFreeColumn,
  removeAggregationTableColumnAtEachRowE,
  updateFreeColumnAtE,
  updateFreeColumnAtEachRowE,
} from '../../../../state/table-element-operations';
import { PlotProps } from '../../commons/plots/helpers';
import { DashboardTable } from '../shared/DashboardTable';
import { useTableInstanceRegistration } from '../shared/useTableInstanceRegistration';
import { useMultiIntervalElementMeasureRows } from '../shared/useMultiIntervalElementMeasureRows';

const hasHorodate = (data: MeasureType[]) => {
  const firstRow = data.find((series) => series.length > 0)?.[0];
  return firstRow != null && 'horodate' in firstRow;
};
const isAggregationTable = not(hasHorodate);

const determineNextNumber = (names: string[], defaultName: string) => {
  // regex pattern to match `<defaultName>` or `<defaultName> (<someNumber>)`
  const pattern = new RegExp('^' + defaultName + '( \\((\\d+)\\))?$');

  const numbers = names
    .map(function (name) {
      return name.match(pattern);
    })
    .filter(function (match) {
      return match !== null;
    })
    .map(function (match) {
      return match[2] ? parseInt(match[2]) : 1;
    });

  return numbers.length > 0 ? Math.max.apply(null, numbers) + 1 : 1;
};

const aggregationPrefixes = [
  'first_quartile',
  'third_quartile',
  'mean',
  'sum',
  'count',
  'median',
  'min',
  'max',
] as const;

type AggPrefixes = (typeof aggregationPrefixes)[number];

const removeAggregationPrefix = (colName: string) =>
  aggregationPrefixes.reduce(
    (acc, curr) => acc.replace(`${curr}_`, ''),
    colName
  );
const keepOnlyAggregationPrefix = (colName: string) =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  aggregationPrefixes.find((aggName) => colName.startsWith(aggName))!;

const toAggregationPrettyName = (agg: AggPrefixes) => {
  switch (agg) {
    case 'count':
      return 'Nombre';
    case 'first_quartile':
      return 'Premier quartile';
    case 'max':
      return 'Max';
    case 'mean':
      return 'Moyenne';
    case 'median':
      return 'Médiane';
    case 'min':
      return 'Min';
    case 'sum':
      return 'Somme';
    case 'third_quartile':
      return 'Troisième quartile';
    default:
      return agg;
  }
};

const formatNumber = (value: number): string => value.toFixed(2);

function insertValuesAtIndices<T>(
  insertions: { insert_index: number; value: T }[],
  arr: T[]
): T[] {
  insertions.slice().sort((a, b) => a.insert_index - b.insert_index);

  return insertions.reduce((result, { insert_index, value }) => {
    const newResult = [
      ...result.slice(0, insert_index),
      value,
      ...result.slice(insert_index),
    ];
    return newResult;
  }, arr);
}

function insertValuesAtRows(
  insertions: AdditionalColumn<string>[],
  originalData: Record<string, Record<string, string>>
): Record<string, Record<string, string>> {
  const newData: typeof originalData = JSON.parse(JSON.stringify(originalData));
  insertions.forEach((insertion) => {
    Object.entries(newData).forEach(([key, values]) => {
      const col = insertion.name;
      const val = insertion.value_by_row[key];
      values[col] = val;
    });
  });
  return newData;
}

const isBeingEdited = (
  editing: EditionSelection,
  rowIndex: number,
  colIndex: number
): boolean => editing.rowIndex === rowIndex && editing.colIndex === colIndex;

interface ColumnHeaderProps {
  key?: string;
  columnName: string;
  editing: boolean;
  removable: boolean;
  onAdd: () => void;
  onRemove: () => void;
  handleHeaderClick: (colIndex: number) => void;
  handleEdit: ReactEventHandler<HTMLInputElement>;
  handleKeyPress: KeyboardEventHandler<HTMLInputElement>;
  handleBlur: FocusEventHandler<HTMLInputElement>;
  index: number;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  key,
  columnName,
  editing,
  removable,
  onAdd,
  onRemove,
  handleKeyPress,
  handleEdit,
  handleBlur,
  handleHeaderClick,
  index,
}) => {
  const handleAdd: ReactEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onAdd();
  };
  const handleRemove: ReactEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onRemove();
  };
  return (
    <TableCell key={key} onClick={() => handleHeaderClick(index)}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1 }}>
          {editing ? (
            <input
              type="text"
              value={columnName}
              onKeyUp={handleKeyPress}
              onChange={handleEdit}
              onBlur={handleBlur}
              autoFocus
              style={{ width: '100%' }}
            />
          ) : (
            columnName
          )}
        </span>
        <ButtonGroup size="small" sx={{ ml: 1 }}>
          {removable ? (
            <Button size="small" variant="outlined" onClick={handleRemove}>
              -
            </Button>
          ) : null}
          <Button size="small" variant="outlined" onClick={handleAdd}>
            +
          </Button>
        </ButtonGroup>
      </div>
    </TableCell>
  );
};

const buildAggregationTable = ({
  data,
  gridId,
  gridType,
  widgetId,
  element,
  setElement,
  editing,
  setEditing,
  editingHeader,
  setEditingHeader,
  dispatch,
  intervalNames,
}: {
  data: MeasureType[];
  gridType: 'grids' | 'reports';
  gridId: number;
  widgetId: string;
  element: TableConfigured;
  setElement: React.Dispatch<React.SetStateAction<ApiDashboardElement>>;
  editing: EditionSelection;
  setEditing: React.Dispatch<React.SetStateAction<EditionSelection>>;
  editingHeader: number | null;
  setEditingHeader: React.Dispatch<React.SetStateAction<number | null>>;
  dispatch: DashboardLibraryDispatch;
  intervalNames: string[];
}) => {
  const additionalColumns = getAdditionalColumns(element);
  const additionalColumnNames = additionalColumns.map((col) => ({
    insert_index: col.insert_index,
    value: col.name,
  }));
  const editableColumns = new Set(additionalColumns.map((c) => c.insert_index));

  const first: Record<string, string | number | boolean | null> | undefined =
    (data.find((d) => d.length > 0) ?? [])[0]; // use first time range with data to determine column names
  const ranges = intervalNames;
  const colNames = first ? Object.keys(first) : []; // ['min_a', 'max_a', 'min_b', 'max_b', /* ... */];
  const colGroupedByMeasure = groupBy(colNames, removeAggregationPrefix); // { 'a': ['min_a', 'max_a'], 'b': ['min_b', 'max_b'], /* ... */ }
  const finalColumns = ['Groupe'].concat(
    unique(colNames.map(keepOnlyAggregationPrefix))
  ); // ['Groupe', 'min', 'max', /* ... */];
  const finalColumnPrettyName = ['Groupe'].concat(
    finalColumns.slice(1).map((_) => toAggregationPrettyName(_ as AggPrefixes))
  ); // ['Groupe', 'Min', 'Max', /* ... */];
  const finalColumnPrettyNameWithAdditions = insertValuesAtIndices(
    additionalColumnNames,
    finalColumnPrettyName
  );
  const finalColumnsWithAdditions = insertValuesAtIndices(
    additionalColumnNames,
    finalColumns
  );

  const rangesWithData = ranges
    .map((r, i) => [r, data[i]] as [string, MeasureType | undefined])
    .map(([r, rangeData]) =>
      rangeData === undefined ||
      rangeData.length === 0 ||
      rangeData[0] === undefined
        ? ([r, [fromArray(colNames.map((n) => [n, ''] as [string, '']))]] as [
            string,
            MeasureType
          ])
        : ([r, rangeData] as [string, MeasureType])
    );

  const dataByMeasure = rangesWithData
    .map(([rangeName, data]) => {
      const first = data[0];

      const dataByMeasure = map(
        // keep the record keys, change the values
        (cols) =>
          fromArray(
            // create sub record from an array of tuples
            cols.map(
              (col) =>
                [keepOnlyAggregationPrefix(col), first[col]!] as [
                  string,
                  string
                ] // aggregation -> value
            )
          ),
        colGroupedByMeasure
      ); // { 'a': { 'min': 1, 'max': 2 }, 'b': { 'min': 3, 'max': 4 }, /* ... */ };
      const dataByMeasureWithRange = mapKeys(
        (measureName) => `${measureName} (${rangeName})`,
        dataByMeasure
      ); // { 'a (Jan)': { 'min': 1, 'max': 2 }, 'b (Jan)': { 'min': 3, 'max': 4 }, /* ... */ };
      return dataByMeasureWithRange;
    })
    .reduce((acc, curr) => {
      return { ...acc, ...curr };
    }, {} as Record<`${string} (${string})`, Record<string, string>>);

  const dataByMeasureWithAdditions = insertValuesAtRows(
    additionalColumns,
    dataByMeasure
  );
  const displayRows = toArray(dataByMeasureWithAdditions);

  const resolveConfigRowIndex = (displayRowIndex: number): number | null => {
    const measureKey = displayRows[displayRowIndex]?.[0];
    if (!measureKey) {
      return null;
    }
    const config = element.properties.config;
    if (!isAggregatedTable(config)) {
      return null;
    }
    const rowIndex = config.rows.findIndex((row) => {
      const columnName = row.columns.find(isAggregatedColumn)?.column_name;
      return columnName != null && measureKey.startsWith(`${columnName} (`);
    });
    return rowIndex >= 0 ? rowIndex : null;
  };

  const handleCellClick = (rowIndex: number, colIndex: number) => {
    if (editableColumns.has(colIndex + 1)) {
      setEditing({ rowIndex, colIndex });
    }
  };

  const handleHeaderClick = (colIndex: number) => {
    if (editableColumns.has(colIndex)) {
      setEditingHeader(colIndex);
    }
  };

  const handleEdit: ReactEventHandler<HTMLInputElement> = (e) => {
    const { rowIndex, colIndex } = editing;
    if (rowIndex !== null && colIndex !== null) {
      const configRowIndex = resolveConfigRowIndex(rowIndex);
      if (configRowIndex === null) {
        return;
      }
      const value = e.currentTarget.value;
      const newElement = updateFreeColumnAtE(
        element,
        configRowIndex,
        colIndex + 1,
        value
      );
      setElement(newElement);
    } else if (editingHeader !== null) {
      const value = e.currentTarget.value;
      const newElement = updateFreeColumnAtEachRowE(
        element,
        editingHeader,
        value
      );
      setElement(newElement);
    }
  };

  const handleKeyPress: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      handleEdit(e);
      setEditing({ rowIndex: null, colIndex: null });
      setEditingHeader(null);
      dispatch(
        updateAndPersistDashboardElement({
          gridId,
          gridType,
          widgetId,
          dashboardElement: element,
        })
      );
    }
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    handleEdit(e);
    setEditing({ rowIndex: null, colIndex: null });
    setEditingHeader(null);
    dispatch(
      updateAndPersistDashboardElement({
        gridId,
        gridType,
        widgetId,
        dashboardElement: element,
      })
    );
  };

  const handleAddColumn = (index: number) => {
    // Get next available default name
    const defaultName = 'Nouvelle colonne';
    const freeColumns =
      (getAggregationTableColumnsByPredicateE(
        element,
        isFreeColumn
      )[0] as FreeColumnJSON[]) ?? [];
    const freeColumnNames = freeColumns.map((col) => col.column_name);
    const nextNumber = determineNextNumber(freeColumnNames, defaultName);
    const suffix = nextNumber > 1 ? ` (${nextNumber})` : '';
    // Add column using default name
    const colonne = makeFreeColumn('', defaultName + suffix);
    const updated = insertFreeColumnAtEachRowE(element, index + 1, colonne);
    setElement(updated);
    dispatch(
      updateAndPersistDashboardElement({
        gridId,
        gridType,
        widgetId,
        dashboardElement: updated,
      })
    );
  };

  const handleRemoveColumn = (index: number) => {
    const updated = removeAggregationTableColumnAtEachRowE(element, index);
    setElement(updated);
    dispatch(
      updateAndPersistDashboardElement({
        gridId,
        gridType,
        widgetId,
        dashboardElement: updated,
      })
    );
  };

  const heading = (
    <TableHead>
      <TableRow>
        {finalColumnPrettyNameWithAdditions.map((col, index) => (
          <ColumnHeader
            key={col}
            columnName={col}
            editing={index === editingHeader}
            removable={editableColumns.has(index)}
            onAdd={() => handleAddColumn(index)}
            onRemove={() => handleRemoveColumn(index)}
            handleHeaderClick={handleHeaderClick}
            handleEdit={handleEdit}
            handleBlur={handleBlur}
            handleKeyPress={handleKeyPress}
            index={index}
          />
        ))}
      </TableRow>
    </TableHead>
  );
  const body = (
    <TableBody>
      {displayRows.map(([measure, values], rowIndex) => (
        <TableRow hover key={measure}>
          <TableCell>{measure}</TableCell>
          {finalColumnsWithAdditions.slice(1).map((col, colIndex) => (
            <TableCell
              key={col}
              onClick={() => handleCellClick(rowIndex, colIndex)}
            >
              {isBeingEdited(editing, rowIndex, colIndex) ? (
                <input
                  type="text"
                  value={
                    ![undefined, null].includes(values[col] as any)
                      ? typeof values[col] === 'number'
                        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          formatNumber(values[col]! as unknown as number)
                        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          values[col]!.toString()
                      : '--'
                  }
                  onKeyUp={handleKeyPress}
                  onChange={handleEdit}
                  onBlur={handleBlur}
                  autoFocus
                  style={{ width: '100%' }}
                />
              ) : ![undefined, null, ''].includes(values[col] as any) ? (
                typeof values[col] === 'number' ? (
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  formatNumber(values[col]! as unknown as number)
                ) : (
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  values[col]!.toString()
                )
              ) : (
                '--'
              )}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
  return displayRows.length === 0 ? (
    <Alert severity="info" sx={{ m: 1 }}>
      Tableau non affiché car il n&apos;y a pas de données
    </Alert>
  ) : (
    <DashboardTable>
      {heading}
      {body}
    </DashboardTable>
  );
};

const buildHorodatedTable = (data: MeasureType[]) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const first = data[0]!;
  const columns = Object.keys(first); // ['horodate', 'a', 'b', /* ... */];
  const heading = (
    <TableHead>
      <TableRow>
        {columns.map((col) => (
          <TableCell key={col}>{col}</TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
  const body = (
    <TableBody>
      {data.flat().map((row) => (
        <TableRow hover key={row['horodate'] as string}>
          {columns.map((col) => (
            <TableCell key={col}>{row[col]}</TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
  return (
    <DashboardTable>
      {heading}
      {body}
    </DashboardTable>
  );
};

interface EditionSelection {
  rowIndex: number | null;
  colIndex: number | null;
}

function measureRowsToMeasureType(rows: MeasureRow[]): MeasureType {
  return rows as MeasureType;
}

function AggregationTable(props: PlotProps, ref: LegacyRef<HTMLDivElement>) {
  const dispatch = useDashboardLibraryDispatch();
  const { grid_id } = useDashboardScope();
  const { grid_type } = useDashboardHost();
  const gridId = grid_id ?? NaN;
  const [editing, setEditing] = useState<EditionSelection>({
    rowIndex: null,
    colIndex: null,
  });
  const [editingHeader, setEditingHeader] = useState<number | null>(null);
  const { elementData: dashboardElement } = props;
  const [currentDashboardElement, setCurrentDashboardElement] = useState(
    dashboardElement as ApiDashboardElement
  );

  useEffect(() => {
    setCurrentDashboardElement(dashboardElement as ApiDashboardElement);
  }, [dashboardElement]);

  const { dataByInterval, loading, error } = useMultiIntervalElementMeasureRows(
    {
      element: dashboardElement,
    }
  );

  useTableInstanceRegistration({
    dashboardElementId: props.dashboardElementId,
    widgetId: props.widget_id,
    enabled: !loading && !error,
  });

  const intervalNames = useMemo(
    () => resolveElementIntervalNames(dashboardElement),
    [dashboardElement]
  );

  const elementData = dataByInterval?.map(measureRowsToMeasureType) ?? null;

  if (loading) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent
          name={dashboardElement.name ?? 'Tableau'}
          message="Chargement en cours"
          spinner
        />
      </div>
    );
  }

  if (error) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent
          name={dashboardElement.name ?? 'Tableau'}
          message={error.message}
        />
      </div>
    );
  }

  const table =
    dashboardElement && elementData ? (
      isAggregationTable(elementData) ? (
        buildAggregationTable({
          data: elementData,
          gridId,
          gridType: grid_type as 'grids' | 'reports',
          widgetId: props.widget_id,
          element: currentDashboardElement as TableConfigured,
          setElement: setCurrentDashboardElement,
          editing,
          setEditing,
          editingHeader,
          setEditingHeader,
          dispatch,
          intervalNames,
        })
      ) : (
        buildHorodatedTable(elementData)
      )
    ) : (
      <div>Erreur de récupération de configuration</div>
    );

  return (
    <div ref={ref} className="widget-content">
      <div>{table}</div>
    </div>
  );
}

export default forwardRef(AggregationTable);
